import { useRef, useState, useEffect, type ChangeEvent } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Cropper, RectangleStencil } from "react-advanced-cropper";
import type { CropperRef } from "react-advanced-cropper";
import "react-advanced-cropper/dist/style.css";
import Button from "./button";
import IconReplaceImage from "@/components/icons/icon-replace-image";
import IconRecycleBinThrow from "@/components/icons/icon-recycle-bin-throw";

interface ImageUploadFieldProps {
	label?: string;
	value: string | null;
	onChange: (croppedImage: string | null) => void;
	error?: string;
	aspectRatio?: number;
	placeholder?: React.ReactNode;
	className?: string;
	maxSizeMB?: number;
	maxDimension?: number; // Maximum width or height in pixels (image will be scaled down if larger)
	outputSize?: number; // Force output to a specific square size in pixels (e.g., 32x32)
	variant?: "light" | "dark";
	loading?: boolean;
	disabled?: boolean;
	enablePadding?: boolean; // Enable padding controls for logos
	allowedTypes?: string[];
	accept?: string;
}

const validateFile = (file: File, maxSizeMB: number, allowedTypes: string[]): string | null => {
	const maxSize = maxSizeMB * 1024 * 1024;

	if (!allowedTypes.includes(file.type)) {
		if (allowedTypes.length === 1 && allowedTypes[0] === "image/png") {
			return "Please upload a valid PNG image";
		}
		return "Please upload a valid image file (JPEG, PNG, or WebP)";
	}
	if (file.size > maxSize) {
		return `Image must be less than ${maxSizeMB}MB`;
	}
	return null;
};

async function getCroppedImageWithPadding(
	canvas: HTMLCanvasElement,
	mimeType: string,
	maxDimension?: number,
	paddingPercent: number = 0,
	outputSize?: number
): Promise<string> {
	const ctx = canvas.getContext("2d");
	if (!ctx) {
		throw new Error("No 2d context");
	}

	let outputWidth = canvas.width;
	let outputHeight = canvas.height;

	// Calculate padding in pixels
	const paddingX = (outputWidth * paddingPercent) / 100;
	const paddingY = (outputHeight * paddingPercent) / 100;

	// Create new canvas with padding
	const paddedCanvas = document.createElement("canvas");
	paddedCanvas.width = outputWidth + paddingX * 2;
	paddedCanvas.height = outputHeight + paddingY * 2;
	const paddedCtx = paddedCanvas.getContext("2d");

	if (!paddedCtx) {
		throw new Error("No 2d context");
	}

	// Fill with transparent background (or white if JPEG)
	const supportsTransparency = ["image/png", "image/webp", "image/gif"].includes(mimeType);
	if (!supportsTransparency) {
		paddedCtx.fillStyle = "#ffffff";
		paddedCtx.fillRect(0, 0, paddedCanvas.width, paddedCanvas.height);
	}

	// Draw the cropped image with padding offset
	paddedCtx.drawImage(canvas, paddingX, paddingY, outputWidth, outputHeight);

	if (outputSize) {
		const resizedCanvas = document.createElement("canvas");
		resizedCanvas.width = outputSize;
		resizedCanvas.height = outputSize;
		const resizedCtx = resizedCanvas.getContext("2d");

		if (!resizedCtx) {
			throw new Error("No 2d context");
		}

		if (!supportsTransparency) {
			resizedCtx.fillStyle = "#ffffff";
			resizedCtx.fillRect(0, 0, resizedCanvas.width, resizedCanvas.height);
		}

		resizedCtx.drawImage(paddedCanvas, 0, 0, outputSize, outputSize);
		const outputFormat = supportsTransparency ? "image/png" : "image/jpeg";
		const quality = outputFormat === "image/jpeg" ? 0.9 : undefined;
		return resizedCanvas.toDataURL(outputFormat, quality);
	}

	// Resize if maxDimension is specified and image exceeds it
	if (maxDimension && (paddedCanvas.width > maxDimension || paddedCanvas.height > maxDimension)) {
		const aspectRatio = paddedCanvas.width / paddedCanvas.height;
		const resizedCanvas = document.createElement("canvas");
		let resizedWidth: number;
		let resizedHeight: number;

		if (paddedCanvas.width > paddedCanvas.height) {
			resizedWidth = maxDimension;
			resizedHeight = maxDimension / aspectRatio;
		} else {
			resizedHeight = maxDimension;
			resizedWidth = maxDimension * aspectRatio;
		}

		resizedCanvas.width = resizedWidth;
		resizedCanvas.height = resizedHeight;
		const resizedCtx = resizedCanvas.getContext("2d");

		if (!resizedCtx) {
			throw new Error("No 2d context");
		}

		// Fill background if needed
		if (!supportsTransparency) {
			resizedCtx.fillStyle = "#ffffff";
			resizedCtx.fillRect(0, 0, resizedCanvas.width, resizedCanvas.height);
		}

		resizedCtx.drawImage(paddedCanvas, 0, 0, resizedWidth, resizedHeight);
		const outputFormat = supportsTransparency ? "image/png" : "image/jpeg";
		const quality = outputFormat === "image/jpeg" ? 0.9 : undefined;
		return resizedCanvas.toDataURL(outputFormat, quality);
	}

	const outputFormat = supportsTransparency ? "image/png" : "image/jpeg";
	const quality = outputFormat === "image/jpeg" ? 0.9 : undefined;

	return paddedCanvas.toDataURL(outputFormat, quality);
}

export default function ImageUploadField({
	label,
	value,
	onChange,
	error,
	aspectRatio,
	placeholder,
	className = "",
	maxSizeMB = 5,
	maxDimension,
	outputSize,
	variant = "light",
	loading = false,
	disabled = false,
	enablePadding = false,
	allowedTypes = ["image/jpeg", "image/png", "image/webp"],
	accept = "image/jpeg,image/png,image/webp",
}: ImageUploadFieldProps) {
	const isDark = variant === "dark";
	const inputRef = useRef<HTMLInputElement>(null);
	const cropperRef = useRef<CropperRef>(null);

	const [isDialogOpen, setIsDialogOpen] = useState(false);
	const [imageSrc, setImageSrc] = useState<string | null>(null);
	const [imageMimeType, setImageMimeType] = useState<string>("image/jpeg");
	const [validationError, setValidationError] = useState<string | null>(null);
	const [imageLoaded, setImageLoaded] = useState(false);
	const [currentImageUrl, setCurrentImageUrl] = useState<string | null>(null);
	const [padding, setPadding] = useState<number>(0);

	// Reset imageLoaded when value changes to a new URL
	useEffect(() => {
		if (value !== currentImageUrl) {
			setCurrentImageUrl(value);
			if (value) {
				setImageLoaded(false);
			}
		}
	}, [value, currentImageUrl]);

	const handleFieldClick = () => {
		if (disabled) return;
		inputRef.current?.click();
	};

	const handlePreviewImageLoad = () => {
		setImageLoaded(true);
	};

	const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
		if (disabled) return;
		const file = e.target.files?.[0];
		if (!file) return;

		const fileError = validateFile(file, maxSizeMB, allowedTypes);
		if (fileError) {
			setValidationError(fileError);
			return;
		}

		setValidationError(null);
		setImageMimeType(file.type);
		const reader = new FileReader();
		reader.onload = () => {
			setImageSrc(reader.result as string);
			setIsDialogOpen(true);
		};
		reader.readAsDataURL(file);

		// Reset input so the same file can be selected again
		if (inputRef.current) {
			inputRef.current.value = "";
		}
	};

	const handleCropConfirm = async () => {
		if (!cropperRef.current) return;

		try {
			const canvas = cropperRef.current.getCanvas({
				minWidth: 0,
				minHeight: 0,
				maxWidth: maxDimension,
				maxHeight: maxDimension,
			});

			if (!canvas) {
				console.error("Failed to get canvas from cropper");
				return;
			}

			let croppedDataUrl: string;
			if (enablePadding && padding > 0) {
				croppedDataUrl = await getCroppedImageWithPadding(canvas, imageMimeType, maxDimension, padding, outputSize);
			} else {
				if (outputSize) {
					const resizedCanvas = document.createElement("canvas");
					resizedCanvas.width = outputSize;
					resizedCanvas.height = outputSize;
					const resizedCtx = resizedCanvas.getContext("2d");

					if (!resizedCtx) {
						throw new Error("No 2d context");
					}

					const supportsTransparency = ["image/png", "image/webp", "image/gif"].includes(imageMimeType);
					if (!supportsTransparency) {
						resizedCtx.fillStyle = "#ffffff";
						resizedCtx.fillRect(0, 0, resizedCanvas.width, resizedCanvas.height);
					}

					resizedCtx.drawImage(canvas, 0, 0, outputSize, outputSize);
					const outputFormat = supportsTransparency ? "image/png" : "image/jpeg";
					const quality = outputFormat === "image/jpeg" ? 0.9 : undefined;
					croppedDataUrl = resizedCanvas.toDataURL(outputFormat, quality);
				} else if (maxDimension && (canvas.width > maxDimension || canvas.height > maxDimension)) {
					// Resize if maxDimension is specified
					const aspectRatio = canvas.width / canvas.height;
					const resizedCanvas = document.createElement("canvas");
					let resizedWidth: number;
					let resizedHeight: number;

					if (canvas.width > canvas.height) {
						resizedWidth = maxDimension;
						resizedHeight = maxDimension / aspectRatio;
					} else {
						resizedHeight = maxDimension;
						resizedWidth = maxDimension * aspectRatio;
					}

					resizedCanvas.width = resizedWidth;
					resizedCanvas.height = resizedHeight;
					const resizedCtx = resizedCanvas.getContext("2d");

					if (!resizedCtx) {
						throw new Error("No 2d context");
					}

					const supportsTransparency = ["image/png", "image/webp", "image/gif"].includes(imageMimeType);
					if (!supportsTransparency) {
						resizedCtx.fillStyle = "#ffffff";
						resizedCtx.fillRect(0, 0, resizedCanvas.width, resizedCanvas.height);
					}

					resizedCtx.drawImage(canvas, 0, 0, resizedWidth, resizedHeight);
					const outputFormat = supportsTransparency ? "image/png" : "image/jpeg";
					const quality = outputFormat === "image/jpeg" ? 0.9 : undefined;
					croppedDataUrl = resizedCanvas.toDataURL(outputFormat, quality);
				} else {
					const supportsTransparency = ["image/png", "image/webp", "image/gif"].includes(imageMimeType);
					const outputFormat = supportsTransparency ? "image/png" : "image/jpeg";
					const quality = outputFormat === "image/jpeg" ? 0.9 : undefined;
					croppedDataUrl = canvas.toDataURL(outputFormat, quality);
				}
			}

			onChange(croppedDataUrl);
			setIsDialogOpen(false);
			setImageSrc(null);
			setPadding(0);
		} catch (err) {
			console.error("Error cropping image:", err);
		}
	};

	const handleDialogClose = () => {
		setIsDialogOpen(false);
		setImageSrc(null);
		setPadding(0);
	};

	const handleClear = () => {
		if (disabled) return;
		onChange(null);
	};

	const handleReplace = () => {
		if (disabled) return;
		inputRef.current?.click();
	};

	const handleZoomIn = () => {
		if (!cropperRef.current) return;
		cropperRef.current.zoomImage(1.2, { transitions: true });
	};

	const handleZoomOut = () => {
		if (!cropperRef.current) return;
		cropperRef.current.zoomImage(0.8, { transitions: true });
	};

	const displayError = validationError || error;

	const loadingSpinner = (
		<div className="flex items-center justify-center">
			<svg className={`w-8 h-8 animate-spin ${isDark ? "text-indigo-400" : "text-brand"}`} viewBox="0 0 24 24" fill="none">
				<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
				<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
			</svg>
		</div>
	);

	const defaultPlaceholder = disabled ? (
		<div className="text-center">
			<svg className={`w-8 h-8 mx-auto ${isDark ? "text-gray-600" : "text-gray-300"}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
				<path
					strokeLinecap="round"
					strokeLinejoin="round"
					strokeWidth="1.5"
					d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
				/>
			</svg>
		</div>
	) : (
		<div className="text-center">
			<svg
				className={`w-10 h-10 mx-auto transition-colors ${isDark ? "text-gray-600 group-hover:text-indigo-400" : "text-gray-300 group-hover:text-brand/80"}`}
				fill="none"
				stroke="currentColor"
				viewBox="0 0 24 24"
			>
				<path
					strokeLinecap="round"
					strokeLinejoin="round"
					strokeWidth="1.5"
					d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
				/>
			</svg>
			<p className={`text-xs mt-2 ${isDark ? "text-gray-500" : "text-gray-400"}`}>Click to upload</p>
		</div>
	);

	return (
		<div className={`${className} ${disabled ? "opacity-60" : ""}`}>
			{label && <label className="block text-sm font-medium text-primary mb-1.5">{label}</label>}

			<input ref={inputRef} type="file" accept={accept} onChange={handleFileChange} className="hidden" disabled={disabled} />

			{loading || (value && !imageLoaded) ? (
				<div
					className={`rounded-xl flex items-center justify-center border ${isDark ? "bg-gray-800 border-gray-700" : "bg-gray-100 border-gray-200"}${!aspectRatio ? " min-h-24" : ""}`}
					style={aspectRatio ? { aspectRatio } : undefined}
				>
					{loadingSpinner}
					{/* Hidden image to trigger load event */}
					{value && !imageLoaded && <img src={value} alt="" onLoad={handlePreviewImageLoad} className="hidden" />}
				</div>
			) : value ? (
				<div className="relative group">
					<div className={`rounded-xl overflow-hidden border ${isDark ? "bg-gray-800 border-gray-700" : "bg-gray-100 border-gray-200"}`} style={aspectRatio ? { aspectRatio } : undefined}>
						<img src={value} alt="Preview" className="w-full h-full object-cover" onLoad={handlePreviewImageLoad} />
					</div>
					{/* Dark backdrop overlay - visible only on hover */}
					{!disabled && (
						<div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 rounded-xl flex items-center justify-center gap-3">
							<button type="button" onClick={handleReplace} className="p-2.5 rounded-lg bg-white/20 hover:bg-white/30 transition-colors" aria-label="Replace image">
								<IconReplaceImage className="w-5 h-5 [&_path]:fill-white" />
							</button>
							<button type="button" onClick={handleClear} className="p-2.5 rounded-lg bg-white/20 hover:bg-white/30 transition-colors" aria-label="Clear image">
								<IconRecycleBinThrow className="w-5 h-5 text-white" />
							</button>
						</div>
					)}
				</div>
			) : (
				<div
					onClick={handleFieldClick}
					className={`rounded-xl flex items-center justify-center border-2 border-dashed transition-colors group ${disabled ? "cursor-not-allowed" : "cursor-pointer"} ${
						isDark ? "bg-gray-800 border-gray-700 hover:border-indigo-500" : "bg-gray-100 border-gray-200 hover:border-brand/50"
					}${!aspectRatio ? " min-h-24" : ""}`}
					style={aspectRatio ? { aspectRatio } : undefined}
				>
					{placeholder || defaultPlaceholder}
				</div>
			)}

			{displayError && <p className="text-xs text-red-500 mt-1">{displayError}</p>}

			<Dialog.Root open={isDialogOpen} onOpenChange={(open) => !open && handleDialogClose()}>
				<Dialog.Portal>
					<Dialog.Overlay className="fixed inset-0 bg-black/50 z-50 modal-overlay" />
					<Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl shadow-xl z-50 w-full max-w-2xl p-6 modal-content max-h-[90vh] overflow-auto">
						<Dialog.Title className="text-lg font-semibold text-gray-900 mb-4">Crop Image</Dialog.Title>

						{imageSrc && (
							<div className="flex flex-col items-center w-full">
								{/* Checkerboard background container */}
								<div
									className="relative w-full rounded-lg overflow-hidden"
									style={{
										backgroundImage: `
											linear-gradient(45deg, #e5e7eb 25%, transparent 25%),
											linear-gradient(-45deg, #e5e7eb 25%, transparent 25%),
											linear-gradient(45deg, transparent 75%, #e5e7eb 75%),
											linear-gradient(-45deg, transparent 75%, #e5e7eb 75%)
										`,
										backgroundSize: "20px 20px",
										backgroundPosition: "0 0, 0 10px, 10px -10px, -10px 0px",
										minHeight: "400px",
										maxHeight: "70vh",
									}}
								>
									<div className="cropper-container" style={{ width: "100%", height: "100%", minHeight: "400px" }}>
										<Cropper
											ref={cropperRef}
											src={imageSrc}
											stencilComponent={RectangleStencil}
											stencilProps={{
												aspectRatio: aspectRatio,
												grid: true,
											}}
											className="cropper"
											backgroundClassName="cropper-background"
											transitions={true}
										/>
									</div>
									{/* Zoom controls */}
									<div className="absolute bottom-4 right-4 flex flex-col gap-2 z-10">
										<button
											type="button"
											onClick={handleZoomIn}
											className="p-2.5 rounded-lg bg-white/90 hover:bg-white shadow-md border border-gray-200 transition-colors flex items-center justify-center"
											aria-label="Zoom in"
										>
											<svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
												<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
											</svg>
										</button>
										<button
											type="button"
											onClick={handleZoomOut}
											className="p-2.5 rounded-lg bg-white/90 hover:bg-white shadow-md border border-gray-200 transition-colors flex items-center justify-center"
											aria-label="Zoom out"
										>
											<svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
												<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
											</svg>
										</button>
									</div>
								</div>

								{/* Padding control */}
								{enablePadding && (
									<div className="w-full mt-4 px-2">
										<label className="block text-sm font-medium text-gray-700 mb-2">
											Padding: {padding}%
										</label>
										<input
											type="range"
											min="0"
											max="30"
											step="1"
											value={padding}
											onChange={(e) => setPadding(Number(e.target.value))}
											className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-brand"
										/>
										<div className="flex justify-between text-xs text-gray-500 mt-1">
											<span>No padding</span>
											<span>30% padding</span>
										</div>
										<p className="text-xs text-gray-500 mt-2">
											Add padding around your logo to prevent edge-to-edge cropping
										</p>
									</div>
								)}
							</div>
						)}

						<div className="flex gap-3 mt-6">
							<Button type="button" variant="soft" className="flex-1" onClick={handleDialogClose}>
								Cancel
							</Button>
							<Button type="button" variant="brand" className="flex-1" onClick={handleCropConfirm}>
								Confirm
							</Button>
						</div>

						<Dialog.Close asChild>
							<button className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors" aria-label="Close">
								<svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
								</svg>
							</button>
						</Dialog.Close>
					</Dialog.Content>
				</Dialog.Portal>
			</Dialog.Root>
		</div>
	);
}
